import { action, makeObservable, observable, runInAction } from 'mobx';
import type { GqlService } from '@services/GqlService';
import type { IsUserAuthorized } from '@services/isUserAuthorized';
import { subscribeUniversal } from '@utils/subscribeUniversal';
import { GetUserDataDocument } from './getUserData.generated';

export type Maybe<T> = T | null | undefined;

export interface UserInfoDict {
  isLoading: boolean;
  error: unknown;
  avatarUrl: Maybe<string>;
  email: Maybe<string>;
  firstName: Maybe<string>;
  lastName: Maybe<string>;
  phone: Maybe<string>;
  login: Maybe<string>;
  isMobileLogged: Maybe<boolean>;
}

export class UserInfoStore implements UserInfoDict {
  @observable
  public isLoading = false;

  @observable
  public error: unknown;

  @observable
  public avatarUrl: Maybe<string> = null;

  @observable
  public email: Maybe<string> = null;

  @observable
  public firstName: Maybe<string> = null;

  @observable
  public lastName: Maybe<string> = null;

  @observable
  public phone: Maybe<string> = null;

  @observable
  public login: Maybe<string> = null;

  @observable
  public isMobileLogged: Maybe<boolean> = null;

  constructor(private gqlService: GqlService, private getIsUserAuthorized: IsUserAuthorized) {
    makeObservable(this);

    subscribeUniversal(
      this,
      [
        'isLoading',
        'error',
        'avatarUrl',
        'email',
        'firstName',
        'lastName',
        'phone',
        'login',
        'isMobileLogged'
      ],
      () => {
        this.updateUserData();
      },
      () => {
        runInAction(() => {
          this.isLoading = false;
          this.error = undefined;
          this.avatarUrl = null;
          this.email = null;
          this.firstName = null;
          this.lastName = null;
          this.phone = null;
          this.login = null;
          this.isMobileLogged = null;
        });
      },
      this.getIsUserAuthorized
    );
  }

  @action
  private updateUserData = () => {
    this.isLoading = true;
    this.error = undefined;

    this.gqlService
      .makeRequest({
        requestOptions: {
          document: GetUserDataDocument
        },
        needAuthorization: true,
        shouldSetEnvAutomatically: false
      })
      .then(({ currentUser, dizraptorMobileRegistrationCollector }) => {
        const userInfo = currentUser?.userInfo;

        if (!userInfo) return;

        const { avatarUrl, email, firstName, lastName, phone, login } = userInfo;

        runInAction(() => {
          this.avatarUrl = avatarUrl;
          this.email = email;
          this.firstName = firstName;
          this.lastName = lastName;
          this.phone = phone;
          this.login = login;
        });

        if (!dizraptorMobileRegistrationCollector) return;

        runInAction(() => {
          this.isMobileLogged = dizraptorMobileRegistrationCollector.isMobileLogged;
        });
      })
      .catch(err => {
        runInAction(() => {
          this.error = err;
        });
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };
}
